import { store } from "../store";
import { createSelector } from "reselect";
import { getTeamDaysAndCosts } from "../store/entities/allocations";
import { getMaterialsCost } from "../store/entities/materials";
import { getTravelCost } from "../store/entities/travel";
import { getCapexCost } from "../store/entities/capex";
import { getOtherCost, getOtherIds } from "../store/entities/other";
import { getFundingLevel, getOverheads } from "../store/entities/project";
import { getWorkPackageIds } from "../store/entities/tasks";
import { roundTo } from "./generalHelpers";

export const getTotalsByCategory = createSelector(
  (state) => state.entities.setup.data,
  (state) => state.entities.tasks.data,
  (state) => state.entities.project.data,
  (state) => state.entities.team.data,
  (state) => state.entities.materials.data,
  (state) => state.entities.travel.data,
  (state) => state.entities.capex.data,
  (state) => state.entities.other.data,
  (setup) => {
    // console.log("getTotalsByCategory");

    const state = store.getState();
    const labour = getTeamDaysAndCosts(state);
    const materialsCost = getMaterialsCost(state);
    const travelCost = getTravelCost(state);
    const capexCost = getCapexCost(state);
    const otherCost = getOtherCost(state);

    const leaderList = ["lead", "pOne", "pTwo", "combined"];

    const leaders = [];
    const staff = [];
    const subcontract = [];
    const travel = [];
    const materials = [];
    const other = [];
    const capex = [];

    leaderList.forEach((leader) => {
      const total =
        labour[leader].cost +
        materialsCost[leader] +
        travelCost[leader] +
        capexCost[leader] +
        otherCost[leader];
      leaders.push(total);
      staff.push(Math.round(labour[leader].staff.cost));
      subcontract.push(Math.round(labour[leader].subcontract.cost));
      travel.push(Math.round(travelCost[leader]));
      materials.push(Math.round(materialsCost[leader]));
      other.push(Math.round(otherCost[leader]));
      capex.push(Math.round(capexCost[leader]));
    });

    const percents = [
      Math.round((leaders[0] / leaders[3]) * 100),
      Math.round((leaders[1] / leaders[3]) * 100),
      Math.round((leaders[2] / leaders[3]) * 100),
      100,
    ];

    const { lead, pOne, pTwo } = state.entities.project.data;
    const leadGrant = Math.floor((leaders[0] * lead.fundingLevel) / 100);
    const pOneGrant = Math.floor((leaders[1] * pOne.fundingLevel) / 100);
    const pTwoGrant = Math.floor((leaders[2] * pTwo.fundingLevel) / 100);
    const totalGrant = leadGrant + pOneGrant + pTwoGrant;
    const grants = [leadGrant, pOneGrant, pTwoGrant, totalGrant];

    const matchFunding = [
      leaders[0] - grants[0],
      leaders[1] - grants[1],
      leaders[2] - grants[2],
      leaders[3] - totalGrant,
    ];

    const summary = {
      leaders,
      percents,
      grants,
      matchFunding,
      labour,
      staff,
      materials,
      travel,
      subcontract,
      capex,
      other,
    };
    // console.log(summary);
    return summary;
  }
);

export const getTotalsByLeader = createSelector(
  (state) => state.entities,
  () => {
    const state = store.getState();
    const labour = getTeamDaysAndCosts(state);
    const overheads = getOverheads(state);
    const materialsCost = getMaterialsCost(state);
    const travelCost = getTravelCost(state);
    const capexCost = getCapexCost(state);
    const otherCost = getOtherCost(state);
    const funding = getFundingLevel(state);

    const totals = {
      lead: 0,
      pOne: 0,
      pTwo: 0,
      combined: 0,
      category: "Total",
    };
    const leaderList = ["lead", "pOne", "pTwo", "combined"];
    leaderList.forEach((leader) => {
      const total =
        Math.round(labour[leader].cost) +
        Math.round(overheads[leader]) +
        Math.round(materialsCost[leader]) +
        Math.round(travelCost[leader]) +
        Math.round(capexCost[leader]) +
        Math.round(otherCost[leader]);
      totals[leader] = total;
    });

    const grants = {};
    grants.lead = Math.floor((funding.lead * totals.lead) / 100);
    grants.pOne = Math.floor((funding.pOne * totals.pOne) / 100);
    grants.pTwo = Math.floor((funding.pTwo * totals.pTwo) / 100);
    grants.combined = grants.lead + grants.pOne + grants.pTwo;
    grants.category = "Grant";

    function people(employment, category) {
      const total = {
        lead: Math.round(labour.lead[employment].cost),
        pOne: Math.round(labour.pOne[employment].cost),
        pTwo: Math.round(labour.pTwo[employment].cost),
        combined: Math.round(labour.combined[employment].cost),
        category,
      };
      return total;
    }

    const array = [
      people("staff", "Labour"),
      overheads,
      materialsCost,
      travelCost,
      people("subcontract", "Subcontract"),
      capexCost,
      otherCost,
      totals,
      grants,
    ];
    const object = {
      staff: people("staff", "Labour"),
      overheads,
      materialsCost,
      travelCost,
      subcontract: people("subcontract", "Subcontract"),
      capexCost,
      otherCost,
      totals,
      grants,
    };
    object.materialsCost.percent = roundTo(
      (object.materialsCost.combined / object.totals.combined) * 100,
      0
    );
    object.travelCost.percent = roundTo(
      (object.travelCost.combined / object.totals.combined) * 100,
      0
    );
    object.capexCost.percent = roundTo(
      (object.capexCost.combined / object.totals.combined) * 100,
      0
    );
    object.otherCost.percent = roundTo(
      (object.otherCost.combined / object.totals.combined) * 100,
      0
    );
    const { lead, pOne, pTwo } = otherCost.breakdown;
    const other = { lead, pOne, pTwo };
    const summary = {
      array,
      object,
      other,
    };
    return summary;
  }
);

export const getWPCost = createSelector(
  (state) => state.entities,
  () => {
    // console.log("getWPCost");
    const state = store.getState();
    const wps = getWorkPackageIds(state);
    const assignments = state.entities.assignments.data;

    const leaders = ["lead", "pOne", "pTwo"];
    const categories = ["materials", "travel", "capex"];

    function getCategoryCosts(leader) {
      const costs = {
        materials: getMaterialsCost(state)[leader],
        travel: getTravelCost(state)[leader],
        capex: getCapexCost(state)[leader],
      };
      const others = getOtherCost(state).breakdown[leader];
      others.forEach((other) => {
        const key = other.otherId;
        costs[key] = other.cost;
      });
      // console.log(costs);
      return costs;
    }

    const result = {};
    result.redundant = [];
    wps.forEach((wp) => {
      result[wp] = {
        combined: 0,
        lead: 0,
        pOne: 0,
        pTwo: 0,
      };
    });

    // console.log(result);

    leaders.forEach((leader) => {
      const costs = getCategoryCosts(leader);
      const otherIds = getOtherIds(state)[leader];
      // console.log(otherIds);
      const leaderCategories = [...categories, ...otherIds];

      leaderCategories.forEach((category) => {
        const categoryCost = costs[category];
        let packsAssigned = [];
        if (assignments[leader][category])
          packsAssigned = assignments[leader][category];
        const packCount = packsAssigned.length;
        const share = Math.round(categoryCost / packCount);
        if (share && share !== Infinity) {
          packsAssigned.forEach((pack) => {
            if (wps.includes(pack)) {
              // work package present
              result[pack].combined = result[pack].combined + share;
              result[pack][leader] = result[pack][leader] + share;
            } else {
              // work package deleted remotely
              const entry = { leader, workPackageId: pack };
              result.redundant.push(entry);
            }
          });
        }
      });
    });
    return result;
  }
);

export const getWPStatus = createSelector(
  (state) => state.entities.assignments.data,
  (state) => state.entities.other.data,
  (state) => state.entities.travel.data,
  (state) => state.entities.capex.data,
  (state) => state.entities.materials.data,
  (assignments) => {
    const state = store.getState();
    const totals = getTotalsByLeader(state);

    const leaders = ["lead", "pOne", "pTwo"];
    const categories = ["materials", "travel", "capex"];

    const result = {
      lead: {
        has: {},
        unassigned: {},
      },
      pOne: {
        has: {},
        unassigned: {},
      },
      pTwo: {
        has: {},
        unassigned: {},
      },
      redundant: [],
    };

    leaders.forEach((leader) => {
      categories.forEach((category) => {
        const key = category + "Cost";
        const hasCategory = totals.object[key][leader] > 0;
        let unassigned = false;
        if (totals.object[key] && hasCategory) {
          unassigned = assignments[leader][category].length === 0;
        }
        result[leader].has[category] = hasCategory;
        result[leader].unassigned[category] = hasCategory && unassigned;
      });

      // collate other Ids and categories (relevant costs)
      const otherIds = getOtherIds(state)[leader];
      const validIds = [...categories, ...otherIds];

      // remove the relevant categories (others) from the list off assignments for flagging
      // and create an object with leader and otherId
      const redundantAssignments = [];
      Object.keys(assignments[leader])
        .filter(function (otherId) {
          return !validIds.includes(otherId);
        })
        .forEach((id) => {
          redundantAssignments.push({ otherId: id, leader });
        });
      result.redundant = [...result.redundant, ...redundantAssignments];

      // process the list
      otherIds.forEach((otherId) => {
        let unassigned = false;
        result[leader].has[otherId] = true;
        if (
          !assignments[leader][otherId] ||
          !assignments[leader][otherId].length
        )
          unassigned = true;
        result[leader].unassigned[otherId] = unassigned;
      });
    });
    return result;
  }
);
